import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import type { OutreachRecipient } from "../lib/types";
import { useScope } from "../scope/ScopeContext";

/**
 * Manager inbox — every customer the funnel routed to the private path, newest
 * first, with an acknowledge workflow. (A note also fires an email to the
 * store's escalation address at submit time; a silent "No" does not, which is
 * exactly why this list has to show them.)
 *
 * Loads `needsManager=true`, NOT `hasFeedback=true`: a customer who answers
 * "Did we do a great job?" with No and then closes the tab leaves no note, and
 * the old filter hid them completely. They are the call most worth making.
 * `feedbackState` is computed server-side (services/manager_queue.ts) so this
 * list and its count can't drift from the database.
 */

interface FollowupCounts {
  unhandled: number;
  handled: number;
  silent: number;
}

interface FeedbackResponse {
  recipients: OutreachRecipient[];
  total?: number;
  followupCounts?: FollowupCounts;
}

export function OutreachFeedbackInboxPage() {
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<FollowupCounts | null>(null);
  const { focusedDealerId } = useScope();
  const [recipients, setRecipients] = useState<OutreachRecipient[]>([]);
  const [showHandled, setShowHandled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ needsManager: "true", limit: "200" });
      if (focusedDealerId) params.set("dealerId", focusedDealerId);
      const res = await apiGet<FeedbackResponse>(
        `/api/outreach/recipients?${params.toString()}`
      );
      setRecipients(
        res.recipients.sort((a, b) => (b.respondedAt ?? "").localeCompare(a.respondedAt ?? ""))
      );
      setTotal(res.total ?? res.recipients.length);
      setCounts(res.followupCounts ?? null);
    } catch (err) {
      console.error("feedback inbox load failed:", err);
    } finally {
      setLoading(false);
    }
  }, [focusedDealerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const acknowledge = async (id: string) => {
    setBusyId(id);
    try {
      await apiPost(`/api/outreach/recipients/${id}/acknowledge-feedback`);
      await load();
    } catch (err) {
      alert(`Acknowledge failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const visible = recipients.filter((r) => showHandled || !r.privateFeedbackAcknowledgedAt);
  // Both numbers come from the server, over the whole inbox. Counting the
  // fetched rows made the headline really "N on this page", which would start
  // under-reporting people owed a call the moment the inbox outgrew its limit.
  const unhandledCount =
    counts?.unhandled ?? recipients.filter((r) => !r.privateFeedbackAcknowledgedAt).length;
  const silentCount =
    counts?.silent ??
    recipients.filter((r) => !r.privateFeedbackAcknowledgedAt && r.feedbackState === "silent")
      .length;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-8 sm:py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Manager feedback</h1>
          <p className="mt-1 text-sm text-slate-500">
            Customers who said we didn't do a great job — {unhandledCount} awaiting follow-up.
            {silentCount > 0 && (
              <> <strong>{silentCount}</strong> left no note; call them anyway.</>
            )}{" "}
            These customers chose to tell you instead of Google.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={showHandled}
            onChange={(e) => setShowHandled(e.target.checked)}
          />
          Show handled
        </label>
      </div>

      <div className="mt-6 space-y-3">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {/* The list is capped; the counts above are not. Say so, or the page
            reads as the whole inbox. */}
        {!loading && total > recipients.length && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
            Showing the {recipients.length} most recent of <strong>{total}</strong>. Focus a
            single store to see the rest.
          </div>
        )}
        {!loading && visible.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
            {showHandled
              ? "Nobody has answered No yet."
              : "Inbox zero — nothing awaiting follow-up."}
          </div>
        )}
        {visible.map((r) => {
          const handled = !!r.privateFeedbackAcknowledgedAt;
          const name = [r.firstName, r.lastName].filter(Boolean).join(" ") || "(no name)";
          return (
            <article
              key={r.id}
              className={`rounded-xl border p-4 shadow-sm ${
                handled ? "border-slate-200 bg-white opacity-70" : "border-rose-200 bg-rose-50"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Link
                    to={`/outreach/recipients/${r.id}`}
                    className="text-sm font-semibold text-slate-900 hover:underline"
                  >
                    {name}
                  </Link>
                  <span className="ml-2 text-xs text-slate-500">
                    {r.dealerName} · {r.type} ·{" "}
                    {r.satisfactionScore ? `${r.satisfactionScore}/5` : "unrated"} ·{" "}
                    {r.respondedAt ? new Date(r.respondedAt).toLocaleString() : ""}
                  </span>
                  <div className="mt-1 text-xs text-slate-500">
                    {[r.phone, r.email].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {handled ? (
                  <span className="shrink-0 text-[11px] text-emerald-700">
                    Handled {new Date(r.privateFeedbackAcknowledgedAt!).toLocaleDateString()}
                  </span>
                ) : (
                  <button
                    onClick={() => void acknowledge(r.id)}
                    disabled={busyId === r.id}
                    className="shrink-0 rounded-md bg-rose-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                  >
                    {busyId === r.id ? "…" : "Mark handled"}
                  </button>
                )}
              </div>
              {r.feedbackState === "silent" ? (
                <p className="mt-2 text-sm italic text-slate-600">
                  Said no, left no note — nothing to read before you call.
                </p>
              ) : (
                <blockquote className="mt-2 whitespace-pre-wrap text-sm text-slate-800">
                  {r.privateFeedback}
                </blockquote>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
